/**
 * Renesia sticky offer launcher.
 *
 * Ported from the CheckoutChamp PDP (rn-sticky / rn-floating-offer). The bar
 * is visible only while no other buy box is on screen: it appears once the
 * first buy-buttons form has scrolled above the viewport and hides again
 * whenever any buy-buttons form is visible, so it never covers a native
 * purchase button. The button opens the panel as a modal dialog: backdrop,
 * Escape and close button, focus trap, body scroll lock, and everything
 * outside the panel and the bar is marked inert.
 *
 * The panel keeps its own blocks (offer widget, buy form) in place; nothing
 * is moved in the DOM, so app widgets keep the bindings they made at init.
 */
class RenesiaStickyOffer extends HTMLElement {
  connectedCallback() {
    this.dock = this.querySelector('[data-rn-dock]');
    this.bar = this.querySelector('[data-rn-bar]');
    this.trigger = this.querySelector('[data-rn-trigger]');
    this.panel = this.querySelector('[data-rn-panel]');
    this.head = this.panel?.querySelector('.renesia-sticky-offer__head');
    this.close = this.querySelector('[data-rn-close]');
    this.backdrop = this.querySelector('[data-rn-backdrop]');
    if (!this.dock || !this.bar || !this.trigger || !this.panel || !this.close || !this.backdrop) return;

    this.opened = false;
    this.savedY = 0;
    this.isolated = [];
    this.abort = new AbortController();
    const { signal } = this.abort;

    this.trigger.addEventListener('click', () => (this.opened ? this.hide() : this.show()), { signal });
    this.close.addEventListener('click', () => this.hide(), { signal });
    this.backdrop.addEventListener('click', () => this.hide(), { signal });
    document.addEventListener('keydown', (e) => this.onKeydown(e), { signal });
    document.addEventListener('focusin', (e) => {
      if (this.opened && !this.panel.contains(e.target) && e.target !== this.trigger) {
        this.close.focus({ preventScroll: true });
      }
    }, { signal });

    window.addEventListener('resize', () => { this.position(); this.sync(); }, { passive: true, signal });
    window.visualViewport?.addEventListener('resize', () => this.position(), { signal });
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(() => this.position());
      this.resizeObserver.observe(this.bar);
    }
    let frame = false;
    window.addEventListener('scroll', () => {
      if (frame) return;
      frame = true;
      requestAnimationFrame(() => { frame = false; this.sync(); });
    }, { passive: true, signal });

    // Close after a successful add so the shopper sees the cart drawer.
    // Horizon's cart drawer fires the standard cart lines event on add.
    document.addEventListener('shopify:cart:lines-update', () => { if (this.opened) this.hide(); }, { signal });

    this.sync();
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.resizeObserver?.disconnect();
    if (this.opened) this.hide();
  }

  /** Rendered buy forms outside this section: the main product form and any
   *  bottom CTA. Zero-size boxes (hidden, or inside a collapsed block) are
   *  ignored so they can neither count as "scrolled past" nor as covering. */
  otherBuyBoxes() {
    return Array.from(document.querySelectorAll('.buy-buttons-block'))
      .filter((el) => !this.contains(el) && el.getBoundingClientRect().height > 0);
  }

  onScreen(el) {
    const r = el.getBoundingClientRect();
    return r.bottom > 0 && r.top < window.innerHeight && r.height > 0;
  }

  sync() {
    const boxes = this.otherBuyBoxes();
    const first = boxes[0];
    // The first buy box may sit in a sticky column that follows the reader
    // down the whole product section, so "scrolled past" is judged on the
    // section that holds it, not on the box itself.
    const firstScope = first?.closest('.shopify-section') || first;
    const passed = this.dataset.showAfter === 'always'
      || !first
      || firstScope.getBoundingClientRect().bottom <= 0;
    const covering = boxes.some((el) => this.onScreen(el));
    const shown = this.opened || (passed && !covering);
    this.toggleAttribute('data-shown', shown);
  }

  position() {
    if (!this.opened) return;
    const gap = window.innerHeight - this.bar.getBoundingClientRect().top + 10;
    this.panel.style.setProperty('--rn-panel-bottom', `${gap}px`);
  }

  isolate() {
    for (let branch = this; branch && branch.parentElement; branch = branch.parentElement) {
      Array.from(branch.parentElement.children).forEach((el) => {
        if (el === branch || /^(SCRIPT|STYLE|LINK)$/.test(el.tagName)) return;
        this.isolated.push([el, el.hasAttribute('inert')]);
        el.setAttribute('inert', '');
      });
      if (branch.parentElement === document.body) break;
    }
  }

  show() {
    this.savedY = window.scrollY;
    this.prevOverflow = document.body.style.overflow;
    this.prevPadding = document.body.style.paddingRight;
    const scrollbar = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.setProperty('--rn-scrollbar-gap', `${scrollbar}px`);
    this.style.setProperty('--rn-scrollbar-gap', `${scrollbar}px`);
    if (scrollbar) {
      document.body.style.paddingRight = `${parseFloat(getComputedStyle(document.body).paddingRight) + scrollbar}px`;
    }
    document.body.style.overflow = 'hidden';

    this.opened = true;
    this.setAttribute('data-open', '');
    this.panel.hidden = false;
    this.backdrop.hidden = false;
    this.panel.setAttribute('role', 'dialog');
    this.panel.setAttribute('aria-modal', 'true');
    this.panel.setAttribute('aria-labelledby', this.head?.querySelector('h2')?.id || '');
    this.trigger.setAttribute('aria-expanded', 'true');
    this.position();
    this.isolate();
    this.panel.scrollTop = 0;
    this.close.focus({ preventScroll: true });
    this.sync();
  }

  hide() {
    if (!this.opened) return;
    this.opened = false;
    this.isolated.forEach(([el, had]) => { if (!had) el.removeAttribute('inert'); });
    this.isolated = [];
    this.removeAttribute('data-open');
    this.panel.hidden = true;
    this.backdrop.hidden = true;
    this.panel.removeAttribute('role');
    this.panel.removeAttribute('aria-modal');
    this.panel.removeAttribute('aria-labelledby');
    this.panel.style.removeProperty('--rn-panel-bottom');
    document.body.style.overflow = this.prevOverflow || '';
    document.body.style.paddingRight = this.prevPadding || '';
    document.body.style.removeProperty('--rn-scrollbar-gap');
    this.style.removeProperty('--rn-scrollbar-gap');
    this.trigger.setAttribute('aria-expanded', 'false');
    window.scrollTo({ top: this.savedY, behavior: 'instant' });
    this.sync();
    this.trigger.focus({ preventScroll: true });
  }

  onKeydown(e) {
    if (!this.opened) return;
    if (e.key === 'Escape') { e.preventDefault(); this.hide(); return; }
    if (e.key !== 'Tab') return;
    const focusable = Array.from(
      this.panel.querySelectorAll('button,a[href],input:not([disabled]),select:not([disabled]),[tabindex="0"]')
    ).filter((el) => el.getClientRects().length && getComputedStyle(el).visibility !== 'hidden');
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!first) return;
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }
}

if (!customElements.get('renesia-sticky-offer')) {
  customElements.define('renesia-sticky-offer', RenesiaStickyOffer);
}
